/* ============================================================================
   ACEPLORE · module-recommendations.js — Shopify Product Recommendations API.

   <lumina-recommendations> renders related/complementary products.

   - If the section was server-rendered with results already (the inline product
     page case), it does nothing but bind editor re-init.
   - Otherwise it lazily (IntersectionObserver) fetches the section standalone via
     routes.product_recommendations_url?section_id=…&product_id=…&intent=…&limit=…
     and swaps the returned grid in. Shopify performs the recommendation lookup
     for that request and re-renders THIS section, so we just extract the inner
     markup. Falls back gracefully (removes the empty shell) on any failure.

   Progressive enhancement: with JS off, the product page still server-renders
   recommendations via {{ recommendations.products }} when available.
   ========================================================================== */
(function () {
  'use strict';

  var Lumina = (window.Lumina = window.Lumina || {});
  var utils = Lumina.utils || {};
  var Base = (Lumina.Element || window.HTMLElement);

  function qs(sel, root) { return (root || document).querySelector(sel); }

  class LuminaRecommendations extends Base {
    connectedCallback() {
      this.url = this.dataset.url || '';
      this.productId = this.dataset.productId || '';
      this.sectionId = this.dataset.sectionId || '';
      this.intent = this.dataset.intent || 'related';
      this.limit = parseInt(this.dataset.limit, 10) || 4;
      this.loaded = false;

      // Already server-rendered with products? Nothing to fetch.
      if (qs('[data-recommendations-grid]', this)) {
        this.loaded = true;
        return;
      }
      // Theme editor: a placeholder is shown server-side and recommendations
      // can't be computed offline — leave it in place, don't fetch/remove.
      if (window.Shopify && window.Shopify.designMode) return;
      // No product context or missing route — can't fetch. Drop the empty shell.
      if (!this.url || !this.productId) { this.dropSection(); return; }

      this.startObserving();
    }

    dropSection() {
      var host = this.closest('[data-section-type="product-recommendations"]') || this;
      host.remove();
    }

    startObserving() {
      if (!('IntersectionObserver' in window)) {
        this.load();
        return;
      }
      this._io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.disconnectObserver();
              this.load();
            }
          });
        },
        { rootMargin: '300px 0px' }
      );
      this._io.observe(this);
      // Ensure observer is also cleaned up if the element is removed.
      if (this._cleanups) this._cleanups.push(() => this.disconnectObserver());
    }

    disconnectObserver() {
      if (this._io) { this._io.disconnect(); this._io = null; }
    }

    buildUrl() {
      var u = new URL(this.url, window.location.origin);
      u.searchParams.set('section_id', this.sectionId);
      u.searchParams.set('product_id', this.productId);
      u.searchParams.set('limit', String(this.limit));
      u.searchParams.set('intent', this.intent);
      return u.toString();
    }

    async load() {
      if (this.loaded) return;
      this.loaded = true;
      this.setAttribute('aria-busy', 'true');
      try {
        var res = await fetch(this.buildUrl());
        if (!res.ok) throw new Error('Recommendations request failed (' + res.status + ')');
        var html = await res.text();
        this.swap(html);
      } catch (err) {
        if (window.console && console.warn) console.warn('[Lumina] recommendations', err);
        this.dropSection();
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    swap(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      // The fetched markup re-renders this whole section; grab its inner element.
      var fresh = doc.querySelector('lumina-recommendations');
      var grid = fresh ? fresh.querySelector('[data-recommendations-grid]') : null;

      // No recommendations available -> drop the section entirely.
      if (!fresh || !grid) {
        this.dropSection();
        return;
      }

      this.innerHTML = fresh.innerHTML;

      // Injected content must NEVER stay invisible: this element itself is
      // .lm-reveal, so reveal it directly, then let the shared reveal system
      // animate the children — with a hard fallback if it's unavailable.
      this.classList.add('is-visible');
      if (Lumina.initReveal) Lumina.initReveal(this);
      else this.querySelectorAll('.lm-reveal').forEach(function (el) { el.classList.add('is-visible'); });
      if (Lumina.events) Lumina.events.emit('section:load', { id: this.sectionId, target: this });
    }
  }

  if (!customElements.get('lumina-recommendations')) {
    customElements.define('lumina-recommendations', LuminaRecommendations);
  }
})();
